import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, Paper, TextField, Typography, IconButton } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { Button } from "components";
import { routeInfo, rowSelected, snackbarData, userCredentials } from "recoilState/GlobalState";
import { Routes as ROUTES } from "consts/Routes-App";
import { SBMDocumentoPreviewGet } from "services/services-mia/sbm/SBMDocumentoPreviewGet";
import { RequestServiceGet } from "services/services-mia/RequestServiceGet";
import { ACTIONS } from "consts/Actions";
import { RequestServiceActions } from "services/services-mia/RequestServiceActions";
import { PDFDocument, PDFHexString, PDFName, PDFNumber, PDFString } from "pdf-lib";
import styles from "./PdfAnnotationPublisher.module.scss";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

const workerExt =
    pdfjs.version.startsWith("4") || parseInt(pdfjs.version, 10) >= 4 ? "mjs" : "js";
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.${workerExt}`;

const createId = () => `pl-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const DEFAULT_SIZES_BY_TYPE = {
    DIGITAL: { width: 160, height: 40 },    // firma digital
    OLOGRAFA: { width: 180, height: 60 },   // firma ológrafa
    TICKET: { width: 480, height: 160 },    // tag de tickets
    DEFAULT: { width: 160, height: 60 }
};

const PdfAnnotationPublisher = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const setRoute = useSetRecoilState(routeInfo);

    const credentials = useRecoilValue(userCredentials);
    const selectedRow = useRecoilValue(rowSelected);
    const setRowSelected = useSetRecoilState(rowSelected);
    const setSnackbarInfo = useSetRecoilState(snackbarData);

    const [pdfUrl, setPdfUrl] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [originalPdfBase64, setOriginalPdfBase64] = useState(null);
    const [documentVersion, setDocumentVersion] = useState(null);
    const [tags, setTags] = useState([]);
    const [placements, setPlacements] = useState([]);
    const [selectedPlacementId, setSelectedPlacementId] = useState(null);
    const [draggingPlacementId, setDraggingPlacementId] = useState(null);
    const [requiredSignerTagIds, setRequiredSignerTagIds] = useState([]);
    const [ticketConfig, setTicketConfig] = useState({ required: false, tag: null });
    const [loading, setLoading] = useState(true);
    const [publishing, setPublishing] = useState(false);
    const [sizeInputValues, setSizeInputValues] = useState({});

    const pageRefs = useRef({});
    const dragOffsetRef = useRef({ x: 0, y: 0 });
    const draggingPageRef = useRef(null);
    const pdfUrlRef = useRef(null);

    const documentIdFromQuery = searchParams.get("documentId");
    const documentId = parseInt(documentIdFromQuery);

    useEffect(() => {
        setRoute(ROUTES.documentTable);
    }, [setRoute]);

    const base64ToBlobUrl = (base64) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        return URL.createObjectURL(blob);
    };

    const loadPdfAndTags = useCallback(async () => {
        if (!documentId || !credentials) return;
        setLoading(true);
        try {
            const docResponse = await SBMDocumentoPreviewGet(documentId, credentials);
            const preview = docResponse.result?.[0];

            if (!preview?.sbmDocumentoPDF) {
                setSnackbarInfo({
                    message: "No se encontró el documento",
                    severity: "error",
                    open: true
                });
                return;
            }

            if (pdfUrlRef.current) {
                URL.revokeObjectURL(pdfUrlRef.current);
            }
            const blobUrl = base64ToBlobUrl(preview.sbmDocumentoPDF);
            pdfUrlRef.current = blobUrl;
            setPdfUrl(blobUrl);
            setOriginalPdfBase64(preview.sbmDocumentoPDF);
            setDocumentVersion(preview.sbmDocumentoVersion);

            // Obtener tags desde SBMDocumentoUsuarioGet (solo firmantes FE/FR)
            const body = {
                service: "SBMDocumentoUsuarioGet",
                params: {
                    sbmDocumentoId: documentId
                }
            };
            const participantes = await RequestServiceGet(body, credentials);

            const firmantes = (participantes ?? []).filter(
                (p) =>
                    ["FE", "FR"].includes(p.SBMRolCod) &&
                    p.SBMDocumentoUsuarioTag &&
                    String(p.SBMDocumentoUsuarioTag).trim() !== ""
            );

            const tagsPorFirmante = firmantes.map((p) => {
                const tipoFirma =
                    p.SBMDocumentoUsuarioTipoFirma === "O"
                        ? "OLOGRAFA"
                        : "DIGITAL";
                return {
                    tag: String(p.SBMDocumentoUsuarioTag).trim(),
                    signerName: p.usuarioNombres ? `${p.usuarioNombres ?? ""} ${p.usuarioApellidos ?? ""}`.trim() : `${p.usuarioNroDeDocumentoX ?? ""}`.trim(),
                    tipoFirma
                };
            });

            const uniqueTagMap = new Map();
            tagsPorFirmante.forEach(({ tag, signerName, tipoFirma }) => {
                const existing = uniqueTagMap.get(tag) || {
                    tag,
                    signers: [],
                    tipoFirma
                };
                if (signerName && !existing.signers.includes(signerName)) {
                    existing.signers.push(signerName);
                }
                uniqueTagMap.set(tag, existing);
            });

            const mappedTagsFromSigners = Array.from(uniqueTagMap.values()).map(
                ({ tag, signers, tipoFirma }) => ({
                    id: tag,
                    name: tag,
                    type: tipoFirma, // DIGITAL u OLOGRAFA → define tamaño por defecto
                    signers
                })
            );

            // Ticket tag (si está configurado en el documento)
            const ticketRequired = preview.sbmTipoDeDocumentoPHTickets === "Y";
            const ticketTag = preview.sbmDocumentoTicketsTag ?? 'TagTicket';
            const ticketTagObj =
                ticketRequired && ticketTag
                    ? {
                        id: ticketTag,
                        name: "Tag de tickets",
                        type: "TICKET",
                        // Usamos signers para mostrar el texto en UI igual que con firmantes
                        signers: ["Tag de tickets"]
                    }
                    : null;

            const allTags = ticketTagObj
                ? [...mappedTagsFromSigners, ticketTagObj]
                : mappedTagsFromSigners;

            setTags(allTags);
            setRequiredSignerTagIds(mappedTagsFromSigners.map((t) => t.id));
            setTicketConfig({ required: ticketRequired, tag: ticketTag });
        } catch (e) {
            setSnackbarInfo({
                message: "Ocurrió un error al cargar el documento o sus tags",
                severity: "error",
                open: true
            });
        } finally {
            setLoading(false);
        }
    }, [documentId, credentials, setSnackbarInfo]);

    useEffect(() => {
        loadPdfAndTags();
        return () => {
            if (pdfUrlRef.current) {
                URL.revokeObjectURL(pdfUrlRef.current);
            }
        };
    }, [loadPdfAndTags]);

    const onDocumentLoadSuccess = useCallback(({ numPages: pages }) => {
        setNumPages(pages);
    }, []);

    const handleTagDragStart = (e, tagId) => {
        e.dataTransfer.setData("application/json", JSON.stringify({ tagId }));
    };

    const handlePageDragOver = (e) => {
        e.preventDefault();
    };

    const handlePageDrop = (e, pageNum) => {
        e.preventDefault();
        const data = e.dataTransfer.getData("application/json");
        if (!data) return;
        let parsed;
        try {
            parsed = JSON.parse(data);
        } catch {
            return;
        }
        const { tagId } = parsed;
        if (!tagId) return;

        const pageEl = pageRefs.current[pageNum];
        if (!pageEl) return;
        const rect = pageEl.getBoundingClientRect();

        const tag = tags.find((t) => String(t.id) === String(tagId));

        // El tag de tickets solo puede ubicarse una vez
        if (
            ticketConfig.tag &&
            String(tagId) === String(ticketConfig.tag) &&
            placements.some((p) => String(p.tagId) === String(ticketConfig.tag))
        ) {
            setSnackbarInfo({
                message: "El tag de tickets solo puede ubicarse una vez en el documento.",
                severity: "warning",
                open: true
            });
            return;
        }
        const defaultSize =
            DEFAULT_SIZES_BY_TYPE[tag?.type?.toUpperCase?.() || "DEFAULT"] ||
            DEFAULT_SIZES_BY_TYPE.DEFAULT;

        const xRaw = e.clientX - rect.left - defaultSize.width / 2;
        const yRaw = e.clientY - rect.top - defaultSize.height / 2;

        const x = Math.max(0, Math.min(xRaw, rect.width - defaultSize.width));
        const y = Math.max(0, Math.min(yRaw, rect.height - defaultSize.height));

        const newPlacement = {
            id: createId(),
            tagId,
            pageNum,
            x,
            y,
            width: defaultSize.width,
            height: defaultSize.height
        };

        setPlacements((prev) => [...prev, newPlacement]);
        setSelectedPlacementId(newPlacement.id);
    };

    const handlePlacementSizeChange = (placementId, field, value) => {
        const key = `${placementId}-${field}`;
        setSizeInputValues((prev) => ({ ...prev, [key]: value }));
        const numeric = Number(value);
        if (value === "" || Number.isNaN(numeric) || numeric <= 0) return;
        setPlacements((prev) =>
            prev.map((p) =>
                p.id === placementId ? { ...p, [field]: numeric } : p
            )
        );
    };

    const handlePlacementSizeBlur = (placementId, field) => {
        const key = `${placementId}-${field}`;
        setSizeInputValues((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const handleRemovePlacement = (placementId) => {
        setPlacements((prev) => prev.filter((p) => p.id !== placementId));
        if (selectedPlacementId === placementId) {
            setSelectedPlacementId(null);
        }
    };

    const handlePlacementMouseDown = (e, placement, pageNum) => {
        e.stopPropagation();
        e.preventDefault();
        const pageEl = pageRefs.current[pageNum];
        if (!pageEl) return;
        const rect = pageEl.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        dragOffsetRef.current = {
            x: mouseX - placement.x,
            y: mouseY - placement.y
        };
        draggingPageRef.current = pageNum;
        setSelectedPlacementId(placement.id);
        setDraggingPlacementId(placement.id);
    };

    useEffect(() => {
        if (!draggingPlacementId) return;

        const handleMouseMove = (e) => {
            const pageNum = draggingPageRef.current;
            const pageEl = pageRefs.current[pageNum];
            if (!pageEl) return;
            const rect = pageEl.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            setPlacements((prev) =>
                prev.map((p) => {
                    if (p.id !== draggingPlacementId) return p;
                    const xRaw = mouseX - dragOffsetRef.current.x;
                    const yRaw = mouseY - dragOffsetRef.current.y;
                    const x = Math.max(0, Math.min(xRaw, rect.width - p.width));
                    const y = Math.max(0, Math.min(yRaw, rect.height - p.height));
                    return { ...p, x, y };
                })
            );
        };

        const handleMouseUp = () => {
            setDraggingPlacementId(null);
            draggingPageRef.current = null;
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [draggingPlacementId]);

    const groupedPlacementsByTag = tags.map((tag) => {
        const tagPlacements = placements.filter(
            (p) => String(p.tagId) === String(tag.id)
        );
        return { tag, placements: tagPlacements };
    });

    // Estado de validaciones para habilitar/deshabilitar el botón de publicar
    const firmaOlografa =
        selectedRow.row.sbmDocumentoTipoFirma === "O" ||
        selectedRow.row.sbmDocumentoTipoFirma === "H";
    const placedTagIds = new Set(placements.map((p) => String(p.tagId)));
    const hasAllSignerTagsPlaced =
        !firmaOlografa ||
        requiredSignerTagIds.length === 0 ||
        requiredSignerTagIds.every((id) => placedTagIds.has(String(id)));
    const { required: ticketRequired, tag: ticketTag } = ticketConfig;
    const hasTicketPlacement =
        !ticketRequired ||
        (ticketTag &&
            placements.some((p) => String(p.tagId) === String(ticketTag)));
    const canConfirm =
        !loading &&
        !publishing &&
        documentVersion &&
        hasAllSignerTagsPlaced &&
        hasTicketPlacement;

    const handleCancel = () => {
        navigate(ROUTES.documentTable.route);
    };

    const handleConfirmPublish = async () => {
        if (!documentId || !credentials || !documentVersion) {
            setSnackbarInfo({
                message: "No se pudo obtener la información del documento a publicar",
                severity: "error",
                open: true
            });
            return;
        }

        // Validar tags de firmantes obligatorios (firma ológrafa o híbrida)
        const firmaOlografa =
            selectedRow.row.sbmDocumentoTipoFirma === "O" ||
            selectedRow.row.sbmDocumentoTipoFirma === "H";
        if (firmaOlografa && requiredSignerTagIds.length > 0) {
            const placedTagIds = new Set(
                placements.map((p) => String(p.tagId))
            );
            const missingSignerTags = requiredSignerTagIds.filter(
                (id) => !placedTagIds.has(String(id))
            );
            if (missingSignerTags.length > 0) {
                setSnackbarInfo({
                    message:
                        "Debe ubicar la marca de firma en el documento para todos los firmantes.",
                    severity: "error",
                    open: true
                });
                return;
            }
        }

        // Validar tag de ticket obligatorio
        const { required: ticketRequired, tag: ticketTag } = ticketConfig;
        if (ticketRequired) {
            const hasTicketPlacement =
                ticketTag &&
                placements.some(
                    (p) => String(p.tagId) === String(ticketTag)
                );
            if (!hasTicketPlacement) {
                setSnackbarInfo({
                    message: "Debe ubicar el tag de ticket en el documento.",
                    severity: "error",
                    open: true
                });
                return;
            }
        }

        // Si el ticket no es obligatorio y no fue ubicado manualmente, insertarlo
        // automáticamente en la parte inferior de la última página
        let effectivePlacements = placements;
        if (!ticketRequired && ticketTag) {
            const hasTicketPlacement = placements.some(
                (p) => String(p.tagId) === String(ticketTag)
            );
            if (!hasTicketPlacement) {
                const lastPage = numPages;
                const pageEl = pageRefs.current[lastPage];
                if (pageEl) {
                    const rect = pageEl.getBoundingClientRect();
                    const ticketSize = DEFAULT_SIZES_BY_TYPE.TICKET;
                    effectivePlacements = [
                        ...placements,
                        {
                            id: createId(),
                            tagId: ticketTag,
                            pageNum: lastPage,
                            x: Math.max(0, (rect.width - ticketSize.width) / 2),
                            y: Math.max(0, rect.height - ticketSize.height - 8),
                            width: ticketSize.width,
                            height: ticketSize.height
                        }
                    ];
                }
            }
        }

        // Construir PDF anotado con los tags colocados
        let annotatedPdfBase64 = originalPdfBase64;
        try {
            if (originalPdfBase64 && effectivePlacements.length > 0) {
                const binaryString = atob(originalPdfBase64);
                const len = binaryString.length;
                const bytes = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const pdfDoc = await PDFDocument.load(bytes);
                const annotsKey = PDFName.of("Annots");

                const placementsByPage = effectivePlacements.reduce((acc, p) => {
                    const pageNum = p.pageNum || 1;
                    if (!acc[pageNum]) acc[pageNum] = [];
                    acc[pageNum].push(p);
                    return acc;
                }, {});

                for (const [pageNumStr, pagePlacements] of Object.entries(
                    placementsByPage
                )) {
                    const pageNum = Number(pageNumStr);
                    const pageIndex = pageNum - 1;
                    const page = pdfDoc.getPage(pageIndex);
                    if (!page) continue;

                    const { width: pageWidth, height: pageHeight } =
                        page.getSize();
                    const pageEl = pageRefs.current[pageNum];
                    if (!pageEl) continue;

                    const rect = pageEl.getBoundingClientRect();
                    const domWidth = rect.width || 1;
                    const domHeight = rect.height || 1;

                    let existingAnnots = page.node.lookup(annotsKey);
                    let annotsArr;
                    if (existingAnnots) {
                        annotsArr = pdfDoc.context.obj([]);
                        for (let i = 0; i < existingAnnots.size(); i++) {
                            const annotRef = existingAnnots.get(i);
                            const annotDict = pdfDoc.context.lookup(annotRef);
                            if (!annotDict) continue;

                            const contents = annotDict.get(PDFName.of("Contents"));
                            let text = "";
                            if (contents instanceof PDFString) {
                                text = contents.asString();
                            } else if (contents instanceof PDFHexString) {
                                text = contents.decodeText();
                            }

                            // Eliminamos anotaciones previas de tags (empiezan con "Tag")
                            if (text && text.startsWith("Tag")) {
                                continue;
                            }

                            annotsArr.push(annotRef);
                        }
                    } else {
                        annotsArr = pdfDoc.context.obj([]);
                    }

                    for (const pl of pagePlacements) {
                        const relX = pl.x / domWidth;
                        const relYTop = pl.y / domHeight;
                        const relWidth = pl.width / domWidth;
                        const relHeight = pl.height / domHeight;

                        const drawWidth = relWidth * pageWidth;
                        const drawHeight = relHeight * pageHeight;
                        const drawX = relX * pageWidth;
                        const drawY = pageHeight * (1 - relYTop - relHeight);

                        const pdfRect = [
                            drawX,
                            drawY,
                            drawX + drawWidth,
                            drawY + drawHeight
                        ];

                        const annotationDict = pdfDoc.context.obj({
                            Type: PDFName.of("Annot"),
                            Subtype: PDFName.of("FreeText"),
                            Rect: pdfDoc.context.obj(
                                pdfRect.map((coord) => PDFNumber.of(coord))
                            ),
                            Contents: PDFString.of(String(pl.tagId)),
                            CA: PDFNumber.of(0), // opacidad 0 → invisible
                            DA: PDFString.of("/F1 1 Tf 0 0 0 rg")
                        });

                        const annotationRef =
                            pdfDoc.context.register(annotationDict);
                        annotsArr.push(annotationRef);
                    }

                    page.node.set(annotsKey, annotsArr);
                }

                const modifiedBytes = await pdfDoc.save();
                let modifiedBinary = "";
                for (let i = 0; i < modifiedBytes.length; i++) {
                    modifiedBinary += String.fromCharCode(modifiedBytes[i]);
                }
                annotatedPdfBase64 = btoa(modifiedBinary);
            }
        } catch (e) {
            setSnackbarInfo({
                message:
                    "Ocurrió un error al generar el PDF anotado para publicar.",
                severity: "error",
                open: true
            });
            return;
        }

        setPublishing(true);
        try {
            const body = {
                service: "SBMDocumentoPublicarDo",
                params: {
                    sbmDocumentoId: documentId,
                    sbmDocumentoVersion: documentVersion,
                    sbmDocumentoPDF: annotatedPdfBase64
                }
            };
            await RequestServiceActions(
                body,
                credentials,
                ACTIONS.UPDATE,
                setSnackbarInfo
            );
            // Al terminar de publicar, volvemos a la tabla y limpiamos la selección
            setRowSelected({ status: false, row: {} });
            navigate(ROUTES.documentTable.route);
        } finally {
            setPublishing(false);
        }
    };

    return (
        <section className={styles.section}>
            <Typography variant="h6" gutterBottom>
                Publicar documento con anotaciones
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
                Arrastra los tags desde la lista de la derecha y suéltalos sobre el PDF
                para definir dónde se aplicarán. Luego confirma la publicación.
            </Typography>

            <Box className={styles.editorLayout}>
                <Box className={styles.pdfArea}>
                    {pdfUrl && !loading && (
                        <Document
                            file={pdfUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            className={styles.document}
                        >
                            {numPages &&
                                Array.from(new Array(numPages), (_, i) => i + 1).map(
                                    (pageNum) => (
                                        <Box
                                            key={pageNum}
                                            ref={(el) => (pageRefs.current[pageNum] = el)}
                                            className={styles.pageWrapper}
                                            onDragOver={handlePageDragOver}
                                            onDrop={(e) => handlePageDrop(e, pageNum)}
                                        >
                                            <Page
                                                pageNumber={pageNum}
                                                renderTextLayer
                                                renderAnnotationLayer
                                                className={styles.page}
                                            />
                                            {placements
                                                .filter((p) => p.pageNum === pageNum)
                                                .map((p) => {
                                                    const tag = tags.find(
                                                        (t) => String(t.id) === String(p.tagId)
                                                    );
                                                    return (
                                                        <div
                                                            key={p.id}
                                                            className={[
                                                                styles.placementBox,
                                                                selectedPlacementId === p.id ? styles.selected : "",
                                                                draggingPlacementId === p.id ? styles.dragging : ""
                                                            ].join(" ").trim()}
                                                            style={{
                                                                left: p.x,
                                                                top: p.y,
                                                                width: p.width,
                                                                height: p.height
                                                            }}
                                                            onMouseDown={(e) =>
                                                                handlePlacementMouseDown(
                                                                    e,
                                                                    p,
                                                                    pageNum
                                                                )
                                                            }
                                                        >
                                                            {tag && tag.signers && tag.signers.length > 0 && (
                                                                <span className={styles.placementLabel}>
                                                                    {tag.signers[0]}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </Box>
                                    )
                                )}
                        </Document>
                    )}
                </Box>

                <Paper className={styles.tagsPanel} elevation={1}>
                    <Typography variant="subtitle1" className={styles.tagsTitle}>
                        Tags del documento
                    </Typography>
                    <Typography variant="body2" className={styles.tagsHelp}>
                        Arrastra un tag y suéltalo sobre una página del PDF. Debajo de
                        cada tag verás en qué páginas fue colocado y podrás ajustar su
                        tamaño.
                    </Typography>

                    <Box className={styles.tagsList}>
                        {groupedPlacementsByTag.map(({ tag, placements: tagPlacements }) => (
                            <Box key={tag.id}>
                                <Box
                                    className={styles.tagItem}
                                    draggable
                                    onDragStart={(e) => handleTagDragStart(e, tag.id)}
                                >
                                    {tag.signers && tag.signers.length > 0 && (
                                        <span className={styles.tagSigner}>
                                            {tag.signers.join(", ")}
                                        </span>
                                    )}
                                </Box>
                                {tagPlacements.length > 0 && (
                                    <Box className={styles.tagPlacements}>
                                        {tagPlacements.map((p) => (
                                            <Box
                                                key={p.id}
                                                className={styles.placementRow}
                                            >
                                                <Box className={styles.placementHeader}>
                                                    <span>
                                                        Página {p.pageNum}
                                                    </span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleRemovePlacement(p.id)
                                                        }
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                <Box className={styles.placementInputs}>
                                                    <TextField
                                                        label="Ancho"
                                                        size="small"
                                                        type="number"
                                                        value={sizeInputValues[`${p.id}-width`] ?? p.width}
                                                        onChange={(e) =>
                                                            handlePlacementSizeChange(
                                                                p.id,
                                                                "width",
                                                                e.target.value
                                                            )
                                                        }
                                                        onBlur={() => handlePlacementSizeBlur(p.id, "width")}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    <TextField
                                                        label="Alto"
                                                        size="small"
                                                        type="number"
                                                        value={sizeInputValues[`${p.id}-height`] ?? p.height}
                                                        onChange={(e) =>
                                                            handlePlacementSizeChange(
                                                                p.id,
                                                                "height",
                                                                e.target.value
                                                            )
                                                        }
                                                        onBlur={() => handlePlacementSizeBlur(p.id, "height")}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        ))}
                        {tags.length === 0 && !loading && (
                            <Typography variant="body2" color="textSecondary">
                                No se encontraron tags configurados para este documento.
                            </Typography>
                        )}
                    </Box>

                    <Box className={styles.footerButtons}>
                        <Button size="big" text="Cancelar" onClick={handleCancel} />
                        <Button
                            size="big"
                            text="Confirmar publicación"
                            onClick={handleConfirmPublish}
                            disabled={!canConfirm}
                        />
                    </Box>
                </Paper>
            </Box>
        </section>
    );
};

export default PdfAnnotationPublisher;

